import { useState, useEffect } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { saveNotesInput, loadNotesInput } from '@/utils/candidateInputAPI'
import { Clock, StickyNote, Lightbulb } from 'lucide-react'

export default function NotesStage() {
  const store = useInterviewStore()
  const [notes, setNotes] = useState('')
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)

  const sessionId = store.sessionId || ''
  const interviewId = store.interviewId || ''

  // Load saved notes on mount (prefer remote over local)
  useEffect(() => {
    const fetchRemoteNotes = async () => {
      if (!interviewId) return
      const remote = await loadNotesInput(interviewId, sessionId)
      if (remote && typeof remote.content === 'string') {
        setNotes(remote.content)
        updateCounts(remote.content)
        setLastSaved(remote.lastSaveAt ? new Date(remote.lastSaveAt) : null)
        if (sessionId) {
          localStorage.setItem(`interview_notes_${sessionId}`, remote.content)
        }
        return
      }
      const savedNotes = sessionId ? localStorage.getItem(`interview_notes_${sessionId}`) : null
      if (savedNotes) {
        setNotes(savedNotes)
        updateCounts(savedNotes)
      }
    }

    fetchRemoteNotes()
  }, [sessionId, interviewId])

  // Auto-save notes
  useEffect(() => {
    if (interviewId) {
      const timeoutId = setTimeout(() => {
        saveNotes('auto')
      }, 2000) // Auto-save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId)
    }
  }, [notes, interviewId])

  const updateCounts = (text: string) => {
    setCharCount(text.length)
    setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0)
  }

  const saveNotes = async (saveType: 'auto' | 'manual' = 'manual') => {
    if (!interviewId) return
    if (!notes.trim()) return
    
    setIsSaving(true)
    try {
      localStorage.setItem(`interview_notes_${sessionId}`, notes)
      const success = await saveNotesInput(interviewId, sessionId, notes, saveType)
      if (success) {
        setLastSaved(new Date())
      }
    } catch (error) {
      console.error('Failed to save notes:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleNotesChange = (value: string) => {
    setNotes(value)
    updateCounts(value)
  }

  const formatLastSaved = () => {
    if (!lastSaved) return 'Not saved yet'
    return `Last saved at ${lastSaved.toLocaleTimeString()}`
  }

  const suggestions = [
    "Questions to ask the interviewer",
    "Key points discussed",
    "Technical concepts mentioned", 
    "Follow-up items",
    "Personal observations",
    "Areas for improvement"
  ]

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <StickyNote className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Interview Notes</h2>
              <p className="text-sm text-gray-600">Take notes during your interview session</p>
            </div>
            {isSaving && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-xs">Saving...</span>
              </div>
            )}
          </div>
          
          <div />
        </div>
        
        {/* Stats */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-6 text-sm text-gray-600">
            <span>{wordCount} words</span>
            <span>{charCount} characters</span>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>{formatLastSaved()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Main Notes Area */}
        <div className="flex-1 p-6">
          <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm">
            <textarea
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Start typing your interview notes here...

You can use this space to:
• Jot down questions from the interviewer
• Record key discussion points
• Note technical concepts or requirements
• Track your thought process
• Write follow-up items

Your notes will auto-save every 2 seconds."
              className="w-full h-full p-6 border-none outline-none resize-none text-gray-700 placeholder-gray-400"
              style={{ fontFamily: 'inherit', fontSize: '16px', lineHeight: '1.6' }}
            />
          </div>
        </div>

        {/* Sidebar with suggestions */}
        <div className="w-80 p-6 bg-white border-l border-gray-200">
          <div className="space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                <h3 className="font-medium text-gray-900">Note-taking Tips</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Quick Actions</h4>
              <div className="space-y-2">
                <button
                  onClick={() => handleNotesChange(notes + '\n\n## ' + new Date().toLocaleTimeString() + '\n')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  📝 Add timestamp
                </button>
                <button
                  onClick={() => handleNotesChange(notes + '\n\n### Key Points:\n- \n- \n- \n')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  📋 Add bullet points
                </button>
                <button
                  onClick={() => handleNotesChange(notes + '\n\n### Questions:\n1. \n2. \n3. \n')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  ❓ Add questions section
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
